import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getNccnGuideLines,
  NccnGuideLinesPayload,
} from "../../../../shared/api/postCall/nccnGuidelines";
import { Item } from "../../../data-model/documentModalWithSideBav";
import { AppDispatch, RootState } from "../../store/store";

/**
 * State interface for NCCN guidelines documents.
 */
interface GuidelinesDocumentsState {
  /** Array of guideline items grouped by J-code */
  data?: Item[];
  /** Whether the guidelines are currently being fetched */
  loading?: boolean;
  /** Error message if fetching failed */
  error?: string;
}

const initialState: GuidelinesDocumentsState = {
  data: undefined,
  loading: false,
  error: undefined,
};

/**
 * Redux slice for managing NCCN guidelines documents state.
 * Handles fetching, storing, and resetting guideline documents for medical orders.
 */
export const guidelinesDocumentsSlice = createSlice({
  name: "guidelinesDocuments",
  initialState,
  reducers: {
    setGuidelinesDocuments: (state, action: PayloadAction<Item[]>) => {
      state.data = action.payload;
      state.loading = false;
      state.error = undefined;
    },
    setGuidelinesLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      state.data = undefined;
      state.error = undefined;
    },
    setGuidelinesError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
      state.data = undefined;
    },
    resetGuidelinesDocuments: (state) => {
      state.data = undefined;
      state.loading = false;
      state.error = undefined;
    },
  },
});

export const {
  setGuidelinesDocuments,
  setGuidelinesLoading,
  setGuidelinesError,
  resetGuidelinesDocuments,
} = guidelinesDocumentsSlice.actions;

/**
 * Thunk action to fetch NCCN guidelines for a given order.
 * Manages loading state and handles errors during the API call.
 *
 * @param payload - The payload containing order_id to fetch guidelines for
 * @returns Thunk action that dispatches appropriate state updates
 */
export const fetchNccnGuidelinesDocuments =
  (payload: NccnGuideLinesPayload) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { guidelinesDocuments } = getState();
    if (guidelinesDocuments.loading) {
      return;
    }
    dispatch(setGuidelinesLoading(true));
    try {
      const response = await getNccnGuideLines(payload);
      dispatch(setGuidelinesDocuments(response.data));
    } catch (error) {
      dispatch(
        setGuidelinesError(
          error instanceof Error ? error.message : "Failed to fetch guidelines",
        ),
      );
    }
  };
